import { DOCUMENT } from "@angular/common";
import { inject, Injectable } from "@angular/core";
import { ThemeConfigResponse } from "../../admin/themes/models/theme.model";

const DEFAULT_TITLE = "Interactive Content Management";
const DEFAULT_TOUCH_ICON = "apple-touch-icon.png";

/**
 * Brands "Add to Home Screen" with the active theme, for pages scoped to a single tenant/theme.
 *
 * Uses Apple's head tags rather than a web app manifest: iOS Safari reads a manifest once per page
 * load and ignores later changes, even a replaced `<link>`, so it can't follow an SPA between
 * tenants. With no manifest, iOS reads the title and touch icon from these tags, and the launch
 * URL from the current route, when the user taps "Add to Home Screen".
 */
@Injectable({ providedIn: "root" })
export class PwaBrandingService {
    private readonly document = inject(DOCUMENT);

    applyTheme(config: ThemeConfigResponse | null): void {
        this.setMeta("apple-mobile-web-app-title", config?.pwaNameShort || config?.pwaName || DEFAULT_TITLE);
        this.setTouchIcon(config?.pwaAppIcon192Url || config?.pwaAppIcon512Url || DEFAULT_TOUCH_ICON);
        this.setMeta("theme-color", config?.pwaThemeColor || null);
    }

    reset(): void {
        this.applyTheme(null);
    }

    private setTouchIcon(href: string): void {
        let link = this.document.head.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
        if (!link) {
            link = this.document.createElement("link");
            link.rel = "apple-touch-icon";
            this.document.head.appendChild(link);
        }
        link.href = href;
    }

    // Null removes the tag, so the browser falls back to its own default.
    private setMeta(name: string, content: string | null): void {
        let meta = this.document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
        if (!content) {
            meta?.remove();
            return;
        }
        if (!meta) {
            meta = this.document.createElement("meta");
            meta.name = name;
            this.document.head.appendChild(meta);
        }
        meta.content = content;
    }
}
